import React, { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownOption } from 'models/Dropdown';
import RefreshIcon from 'icons/Refresh.icon';
import ClipboardcopyIcon from 'icons/Clipboardcopy.icon';
import SettingsIcon from 'icons/Settings.icon';
import CalendarIcon from 'icons/Calendar.icon';
import useCopyToClipboard from 'hooks/useCopyToClipboard';
import { getResolveUrl, getShareableLinkRoute } from 'utils';
import useChangeShareableLinkState, {
  UseShareableLinkActionProps,
} from './useChangeShareableLinkState';
import useManageShareableLink from './useManageShareableLink';
import useProlongSecureLink from 'router/subrouters/DataRoom/pages/Dashboard/componenets/SuggestedActions/hooks/useProlongSecureLink';
import { DataRoomShareableLink } from 'models/ShareableLink';
import ArchiveIcon from 'icons/Archive.icon';

const useGetShareableLinkActionOptions = (
  props: UseShareableLinkActionProps,
) => {
  const { link, updateShareableLink } = props;

  const { _id, linkExpiresDate } = link;

  const { t } = useTranslation();
  const copyToClipboard = useCopyToClipboard();

  const {
    changeState,
    isActive,
    isChangeStateDisabled,
    activateSecureLinkModal,
  } = useChangeShareableLinkState(props);

  const onSuccessProlong = useCallback(
    (shareableLink: Partial<DataRoomShareableLink>) => {
      updateShareableLink?.({ ...link, ...shareableLink });
    },
    [link, updateShareableLink],
  );

  const { openProlongModal, prolongSecureLinkModal } =
    useProlongSecureLink(onSuccessProlong);
  const { manageShareableLink, isManageShareableLinkDisabled } =
    useManageShareableLink(link);

  const options = useMemo<DropdownOption[]>(
    () => [
      {
        label: t('manage'),
        icon: <SettingsIcon strokeWidth="1.6" />,
        disabled: isManageShareableLinkDisabled,
        onClick: manageShareableLink,
      },
      ...(isActive
        ? [
            {
              label: t('extend'),
              icon: <CalendarIcon strokeWidth="1.6" />,
              disabled: isChangeStateDisabled,
              onClick: () => openProlongModal(_id, linkExpiresDate),
            },
            {
              label: t('copyLink'),
              icon: <ClipboardcopyIcon strokeWidth="1.2" />,
              onClick: (e: MouseEvent) =>
                copyToClipboard(e, getResolveUrl(getShareableLinkRoute(_id))),
            },
          ]
        : []),
      {
        label: t(isActive ? 'archive' : 'restore'),
        icon: isActive ? <ArchiveIcon /> : <RefreshIcon />,
        alert: isActive,
        disabled: isChangeStateDisabled,
        onClick: changeState,
      },
    ],
    [
      isActive,
      t,
      isChangeStateDisabled,
      changeState,
      isManageShareableLinkDisabled,
      manageShareableLink,
      openProlongModal,
      _id,
      linkExpiresDate,
      copyToClipboard,
    ],
  );

  return {
    options,
    prolongShareableLinkModal: prolongSecureLinkModal,
    activateSecureLinkModal,
  };
};

export default useGetShareableLinkActionOptions;
